import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Plus,
    Edit,
    Trash2,
    Calendar,
    AlertCircle,
    Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { getJson } from '@/lib/http';
import { RetailerEvent } from '@shared/schema';
import { EventDialog } from './EventDialog';

interface EventsTabProps {
    retailerId: number;
}

export function EventsTab({ retailerId }: EventsTabProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<RetailerEvent | null>(null);

    const { data: eventsResponse, isLoading } = useQuery<{ success: boolean; data: RetailerEvent[] }>({
        queryKey: ['retailer-events', retailerId],
        queryFn: () => getJson<{ success: boolean; data: RetailerEvent[] }>(`/api/retailers/${retailerId}/events`),
    });

    const events = eventsResponse?.data || [];

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return await apiRequest(`/api/retailer-events/${id}`, 'DELETE');
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['retailer-events', retailerId] });
            toast({
                title: 'Success',
                description: 'Event deactivated successfully',
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to deactivate event',
                variant: 'destructive',
            });
        },
    });

    const handleCreate = () => {
        setSelectedEvent(null);
        setIsDialogOpen(true);
    };

    const handleEdit = (event: RetailerEvent) => {
        setSelectedEvent(event);
        setIsDialogOpen(true);
    };

    const handleDelete = (id: number) => {
        if (confirm('Are you sure you want to deactivate this event? It will no longer be available for new bookings.')) {
            deleteMutation.mutate(id);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold">Retailer Events</h3>
                    <p className="text-sm text-muted-foreground">
                        Manage reusable planning events (Easter, Back to School, etc.) for this retailer.
                    </p>
                </div>
                <Button onClick={handleCreate}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Event
                </Button>
            </div>

            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[200px]">Event Name</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="w-[100px]">Status</TableHead>
                                <TableHead className="w-[100px] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {events.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center gap-2">
                                            <Calendar className="h-8 w-8 opacity-20" />
                                            <span>No events created for this retailer yet.</span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                events.map((event: RetailerEvent) => (
                                    <TableRow key={event.id}>
                                        <TableCell className="font-medium">{event.name}</TableCell>
                                        <TableCell className="text-muted-foreground max-w-md truncate">
                                            {event.description || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={event.isActive ? 'default' : 'secondary'}>
                                                {event.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEdit(event)}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    onClick={() => handleDelete(event.id)}
                                                    disabled={!event.isActive}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <EventDialog
                retailerId={retailerId}
                event={selectedEvent}
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
            />
        </div>
    );
}
